import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { ReactRenderer, posToDOMRect } from "@tiptap/react";
import type {
  SuggestionProps,
  SuggestionKeyDownProps,
} from "@tiptap/suggestion";
import {
  computePosition,
  autoUpdate,
  offset,
  flip,
  shift,
  size,
  type VirtualElement,
} from "@floating-ui/dom";
import { Box, Text, useIsMobile } from "@salestrak/ui";
import type { Omittable } from "@salestrak/ui";
import { at } from "#app/lib/utils/at";

export type SuggestionItem = Omittable<{
  id: string;
  title: string;
  description?: string;
  renderAvatar?: () => React.ReactNode;
  section?: string;
  data?: Record<string, unknown>;
}>;

type SuggestionListProps = Omittable<{
  items: SuggestionItem[];
  command: (item: SuggestionItem) => void;
  maxHeight?: number;
  emptyMessage?: string;
  columns?: number;
  width?: number;
  ref?: React.Ref<SuggestionRenderHandle>;
}>;

export interface SuggestionRenderHandle {
  onKeyDown: (props: { event: KeyboardEvent }) => boolean;
}

const POPOVER_WIDTH = 280;
const POPOVER_MAX_HEIGHT = 280;
const MENU_GAP = 8;
const VIEWPORT_PADDING = 8;
const MENU_MIN_HEIGHT = 120;
// Matches the base popup layer (`z-50` on every Positioner) — this panel is a
// hand-positioned popup living beside them.
const POPUP_Z_INDEX = 50;

interface LayoutCell {
  item: SuggestionItem;
  index: number;
}

type LayoutRow = Omittable<{
  header?: string;
  cells: LayoutCell[];
}>;

function buildLayoutRows(items: SuggestionItem[], cols: number): LayoutRow[] {
  const rows: LayoutRow[] = [];
  let i = 0;
  while (i < items.length) {
    const section = at(items, i).section;
    let end = i;
    while (end < items.length && at(items, end).section === section) end++;
    const prevSection = i > 0 ? at(items, i - 1).section : undefined;
    const header = section && section !== prevSection ? section : undefined;
    for (let start = i; start < end; start += cols) {
      rows.push({
        header: start === i ? header : undefined,
        cells: items
          .slice(start, Math.min(end, start + cols))
          .map((item, k) => ({ item, index: start + k })),
      });
    }
    i = end;
  }
  return rows;
}

/**
 * The floating chrome shared by the slash menu and the mention menu: a popup
 * card two surface steps above ambient, matching the base popup idiom.
 */
export function SuggestionPanel({
  maxHeight,
  minWidth,
  maxWidth,
  scroll = false,
  padded = false,
  children,
}: Omittable<{
  maxHeight?: number;
  minWidth?: number;
  maxWidth?: number;
  scroll?: boolean;
  padded?: boolean;
  children: React.ReactNode;
}>) {
  return (
    <Box
      surface="popover"
      border
      rounded="lg"
      maxHeight={maxHeight}
      minWidth={minWidth}
      maxWidth={maxWidth}
      padding={padded ? "xs" : undefined}
      overflow={scroll ? "auto" : "hidden"}
    >
      {children}
    </Box>
  );
}

export function SuggestionList({
  items,
  command,
  maxHeight,
  emptyMessage,
  columns = 1,
  width = POPOVER_WIDTH,
  ref,
}: SuggestionListProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const isMobile = useIsMobile();
  // A two-column panel can't sit beside the caret on a phone — collapse to
  // single column at the default width instead.
  const cols = isMobile ? 1 : Math.max(1, columns);
  const panelWidth = cols < Math.max(1, columns) ? POPOVER_WIDTH : width;

  useEffect(() => setSelectedIndex(0), [items]);

  const rows = useMemo(() => buildLayoutRows(items, cols), [items, cols]);

  const selectItem = (index: number) => {
    const item = items[index];
    if (item) command(item);
  };

  const positionOf = (index: number) => {
    for (let r = 0; r < rows.length; r++) {
      const c = at(rows, r).cells.findIndex((cell) => cell.index === index);
      if (c !== -1) return { r, c };
    }
    return { r: 0, c: 0 };
  };

  useImperativeHandle(ref, () => ({
    onKeyDown: ({ event }) => {
      if (!items.length) return false;
      // Clamp against the current list: filtering can shrink items before the
      // [items] reset effect flushes, so a keydown in that window could read a
      // stale index.
      const current = Math.min(selectedIndex, items.length - 1);
      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        const { r, c } = positionOf(current);
        const nextRow =
          event.key === "ArrowUp"
            ? r > 0
              ? r - 1
              : rows.length - 1
            : (r + 1) % rows.length;
        const cells = at(rows, nextRow).cells;
        setSelectedIndex(at(cells, Math.min(c, cells.length - 1)).index);
        return true;
      }
      // In a single column, Left/Right fall through so the editor keeps
      // moving the caret.
      if (cols > 1 && (event.key === "ArrowLeft" || event.key === "ArrowRight")) {
        const delta = event.key === "ArrowLeft" ? -1 : 1;
        setSelectedIndex((i) =>
          Math.min(items.length - 1, Math.max(0, i + delta)),
        );
        return true;
      }
      if (event.key === "Enter") {
        selectItem(current);
        return true;
      }
      return false;
    },
  }));

  const selectedCellRef = useCallback((node: HTMLElement | null) => {
    node?.scrollIntoView?.({ block: "nearest" });
  }, []);

  return (
    <SuggestionPanel
      scroll
      padded
      maxHeight={maxHeight ?? POPOVER_MAX_HEIGHT}
      minWidth={panelWidth}
      maxWidth={panelWidth}
    >
      {items.length === 0 && (
        <Box direction="column" align="center" justify="center" paddingY="sm" grow>
          <Text size="sm" variant="muted">
            {emptyMessage ?? "No results"}
          </Text>
        </Box>
      )}
      {rows.map((row, rowIndex) => (
        <React.Fragment key={rowIndex}>
          {row.header && (
            <Box
              paddingX="sm"
              paddingTop={rowIndex === 0 ? "2xs" : "sm"}
              paddingBottom="2xs"
            >
              <Text size="xs" variant="muted" weight="medium">
                {row.header.toUpperCase()}
              </Text>
            </Box>
          )}
          <Box direction="row" gap="2xs">
            {row.cells.map(({ item, index }) => (
              <Box
                key={`${item.id}-${index}`}
                as="button"
                ref={index === selectedIndex ? selectedCellRef : undefined}
                grow
                background={index === selectedIndex ? "muted" : "transparent"}
                rounded="md"
                onClick={() => selectItem(index)}
                onPointerEnter={() => setSelectedIndex(index)}
                direction="row"
                align="center"
                gap="sm"
                paddingX="sm"
                paddingY="xs"
                minWidth={0}
              >
                {item.renderAvatar && <Box shrink={false}>{item.renderAvatar()}</Box>}
                <Box direction="column" align="start" grow minWidth={0}>
                  <Text size="sm" truncate>
                    {item.title}
                  </Text>
                  {item.description && (
                    <Text size="xs" variant="muted" truncate>
                      {item.description}
                    </Text>
                  )}
                </Box>
              </Box>
            ))}
            {Array.from({ length: cols - row.cells.length }).map((_, k) => (
              <Box key={`filler-${k}`} grow />
            ))}
          </Box>
        </React.Fragment>
      ))}
    </SuggestionPanel>
  );
}

export function createSuggestionRenderer<P extends object>(opts: {
  component: React.ComponentType<
    P & { maxHeight?: number } & React.RefAttributes<SuggestionRenderHandle>
  >;
  mapProps: (props: SuggestionProps<SuggestionItem>) => P;
  maxHeight?: number;
}) {
  const popoverMaxHeight = opts.maxHeight ?? POPOVER_MAX_HEIGHT;
  return () => {
    let component: ReactRenderer<SuggestionRenderHandle> | null = null;
    let latestProps: SuggestionProps<SuggestionItem> | null = null;
    // Created lazily — until a caret clientRect exists there's nothing to
    // anchor to, so creation/append is deferred to avoid a detached renderer.
    let mounted = false;
    let stopAutoUpdate: (() => void) | null = null;
    let maxHeight = popoverMaxHeight;

    const propsFor = (props: SuggestionProps<SuggestionItem>) => ({
      ...opts.mapProps(props),
      maxHeight,
    });

    const reference: VirtualElement = {
      getBoundingClientRect: () => {
        const rect = latestProps?.clientRect?.();
        if (rect) return rect;
        const editor = latestProps?.editor;
        const range = latestProps?.range;
        if (editor && range) {
          return posToDOMRect(editor.view, range.from, range.to);
        }
        return new DOMRect();
      },
    };

    const update = () => {
      if (!component || !mounted) return;
      const el = component.element as HTMLElement;
      computePosition(reference, el, {
        strategy: "fixed",
        // Prefer above the caret: on iPad the on-screen keyboard overlays the
        // viewport without shrinking it, so a below-caret menu hides behind it.
        // flip() still drops it below when there's no room above.
        placement: "top-start",
        middleware: [
          offset(MENU_GAP),
          flip({ padding: VIEWPORT_PADDING }),
          shift({ padding: VIEWPORT_PADDING }),
          size({
            padding: VIEWPORT_PADDING,
            apply: ({ availableHeight, elements }) => {
              const next = Math.min(
                popoverMaxHeight,
                Math.max(MENU_MIN_HEIGHT, Math.floor(availableHeight)),
              );
              elements.floating.style.maxHeight = `${next}px`;
              if (next !== maxHeight && latestProps) {
                maxHeight = next;
                component?.updateProps(propsFor(latestProps));
              }
            },
          }),
        ],
      }).then(({ x, y, strategy }) => {
        el.style.position = strategy;
        el.style.left = `${x}px`;
        el.style.top = `${y}px`;
        el.style.width = "max-content";
        el.style.zIndex = String(POPUP_Z_INDEX);
      });
    };

    const preventBlur = (e: Event) => e.preventDefault();

    const mount = (props: SuggestionProps<SuggestionItem>) => {
      if (mounted || !props.clientRect) return;
      component = new ReactRenderer(opts.component, {
        props: propsFor(props),
        editor: props.editor,
      });
      document.body.appendChild(component.element);
      // Body-portaled, so it's outside a host modal dialog: a modal layer that
      // sets pointer-events:none on <body> would otherwise make rows unclickable.
      component.element.style.pointerEvents = "auto";
      // Keep the editor focused (suggestion active) on click instead of blurring.
      component.element.addEventListener("mousedown", preventBlur);
      mounted = true;
      // autoUpdate also catches the async growth when server pool results arrive
      // (which does NOT fire a suggestion onUpdate).
      stopAutoUpdate = autoUpdate(reference, component.element, update);
    };

    const teardown = () => {
      stopAutoUpdate?.();
      stopAutoUpdate = null;
      component?.element.removeEventListener("mousedown", preventBlur);
      component?.destroy();
      component = null;
      latestProps = null;
      mounted = false;
      maxHeight = popoverMaxHeight;
    };

    return {
      onStart: (props: SuggestionProps<SuggestionItem>) => {
        latestProps = props;
        maxHeight = popoverMaxHeight;
        mount(props);
      },

      onUpdate: (props: SuggestionProps<SuggestionItem>) => {
        latestProps = props;
        mount(props);
        component?.updateProps(propsFor(props));
        update();
      },

      onKeyDown: (props: SuggestionKeyDownProps) => {
        if (props.event.key === "Escape") {
          teardown();
          return true;
        }
        return component?.ref?.onKeyDown({ event: props.event }) ?? false;
      },

      onExit: () => {
        teardown();
      },
    };
  };
}
